import { type AztecAddress, BatchCall, type ContractFunctionInteraction } from "@aztec/aztec.js";
import type { Hex } from "@turnstile-portal/turnstile.js";
import { getL2ClientAsync } from "../aztec-utils";
import { getL2TokenFromL2Address, getL2TokenWithKnownAddresses } from "../lib/singletons";

export async function getL2PublicBalance(
  l2TokenAddress: AztecAddress,
  l2AccountAddress: AztecAddress,
): Promise<bigint> {
  const aztecToken = await getL2TokenFromL2Address(l2TokenAddress.toString());
  const l2Client = await getL2ClientAsync();
  return await aztecToken.balanceOfPublic(l2AccountAddress).simulate({ from: l2Client.getWallet().getAddress() });
}

export async function getL2PrivateBalance(
  l2TokenAddress: AztecAddress,
  l2AccountAddress: AztecAddress,
): Promise<bigint> {
  const aztecToken = await getL2TokenFromL2Address(l2TokenAddress.toString());
  const l2Client = await getL2ClientAsync();
  return await aztecToken.balanceOfPrivate(l2AccountAddress).simulate({ from: l2Client.getWallet().getAddress() });
}

/**
 * Efficiently batch fetch ONLY public balances.
 * Public balances can be truly batched via BatchCall for better performance.
 */
export async function getL2PublicBalancesBatch(
  tokens: Array<{ l1Address: Hex; l2Address: Hex }>,
  l2AccountAddress: AztecAddress,
): Promise<Map<string, bigint>> {
  const interactions: ContractFunctionInteraction[] = [];

  // Use singleton to cache L2Token instances
  for (const { l1Address, l2Address } of tokens) {
    const l2Token = await getL2TokenWithKnownAddresses(`${l1Address}:${l2Address}`);
    interactions.push(l2Token.balanceOfPublic(l2AccountAddress));
  }

  const l2Client = await getL2ClientAsync();
  const wallet = l2Client.getWallet();
  const batch = new BatchCall(wallet, interactions);
  console.debug(`Fetching public balances for batch of ${tokens.length} tokens...`);
  const results = await batch.simulate({ from: wallet.getAddress() });

  const balances: Map<string, bigint> = new Map();
  for (const [index, { l2Address }] of tokens.entries()) {
    balances.set(l2Address, results[index]);
  }

  return balances;
}
